// @ts-strict-ignore
import { useTranslation } from "react-i18next";

import { Arrange, NoStyleAccordion, Typography, Spacer, Link } from "@aviary";
import { emersonRoutes } from "@clinic/emerson/data/emersonRoutes";
import { useMegaMenuQuery } from "@clinic/shared/data/queries/CatalogMegaMenuQuery/CatalogMegaMenu.query";
import { CategoryLink } from "@emerson/components/CategoryLink/CategoryLink";
import { ShopSpecials } from "@emerson/components/ShopSpecials/ShopSpecials";
import type { WordpressSpecialType } from "@emerson/data/queries/WordpressSpecialsQuery/WordpressSpecialsQuery.query";
import { l } from "@emerson/locales/i18n";
import { AnalyticsEvent } from "@shared/hooks/useAnalyticsLogger/AnalyticsEvent";
import { useAnalyticsLogger } from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";

import { SidebarMenu } from "../SidebarMenu/SidebarMenu";

import { SkeletonShop } from "./SkeletonShop";

import * as styles from "./Shop.styles";

interface Props {
  onClose: () => void;
  specialsData: { specials: WordpressSpecialType[]; showSpecials: boolean };
}

const Shop = ({ onClose, specialsData: { specials, showSpecials } }: Props) => {
  const { t } = useTranslation();
  const logAnalyticsEvent = useAnalyticsLogger();
  const { isUnauthenticated } = useSharedGlobalConfig();

  const { data, loading } = useMegaMenuQuery({
    variables: { kind: "EMERSON" },
  });

  const { menuCategories } = data?.viewer || {};

  const onMenuClick = open => {
    if (open) {
      logAnalyticsEvent({
        eventName: isUnauthenticated
          ? AnalyticsEvent.EMERSON_UNAUTH_SHOP_MENU_DROPDOWN
          : AnalyticsEvent.EMERSON_SHOP_MENU_DROPDOWN,
        payload: {
          origin_url: location.pathname,
          automatic_event: false,
        },
      });
    }
  };

  const renderSubCategories = category =>
    category.subCategories.map(subCategory => (
      <CategoryLink
        key={subCategory.id}
        category={subCategory}
        categoryName={category.name}
        isSubcategory
        onClick={onClose}
      />
    ));

  const renderMenuCategories = () =>
    menuCategories.map(category => {
      const { name, id, subCategories } = category;
      const hasSubCategories = subCategories.length > 1;

      if (hasSubCategories) {
        return (
          <NoStyleAccordion
            triggerStyles={styles.borderLess}
            coreTriggerContent={<Typography>{name}</Typography>}
            key={id}
          >
            <Arrange isVertical css={styles.item}>
              {renderSubCategories(category)}
            </Arrange>
          </NoStyleAccordion>
        );
      }

      return (
        <CategoryLink
          hasOnlyOneSubcategory={subCategories.length === 1}
          category={category}
          categoryName={category.name}
          key={id}
          linkStyles={styles.link}
          onClick={onClose}
        />
      );
    });

  const renderSpecials = () => {
    if (!showSpecials)
      return (
        <Link
          to={emersonRoutes.external.specials}
          isColor="system"
          isUnderLined={false}
          css={styles.link}
          onClick={onClose}
          role="menuitem"
        >
          {t(l.header.Shop.Specials)}
        </Link>
      );

    return (
      <NoStyleAccordion
        size="xsmall"
        triggerStyles={[styles.trigger, styles.borderLess]}
        coreTriggerContent={<Typography isSecondaryWeight>{t(l.header.Shop.Specials)}</Typography>}
        key={t(l.header.Shop.Specials)}
      >
        <ShopSpecials onClose={onClose} specials={specials} />
      </NoStyleAccordion>
    );
  };

  const renderReorderProducts = () => (
    <Link
      isColor="system"
      to={emersonRoutes.orders.reorderProducts}
      isUnderLined={false}
      css={styles.link}
      onClick={onClose}
    >
      <Typography isSecondaryWeight isMarginless>
        {t(l.header.Shop.ReorderProducts)}
      </Typography>
    </Link>
  );

  const renderIsNew = () => (
    <Link
      isColor="system"
      to={emersonRoutes.shop.isNew}
      isUnderLined={false}
      css={styles.link}
      onClick={onClose}
    >
      <Typography isSecondaryWeight isMarginless>
        {t(l.header.Shop.New)}
      </Typography>
    </Link>
  );

  const renderShopMenu = () => {
    if (loading) {
      return <SkeletonShop />;
    }

    return (
      <>
        {renderMenuCategories()}
        {!isUnauthenticated && renderReorderProducts()}
        {renderIsNew()}
        {renderSpecials()}
      </>
    );
  };

  return (
    <SidebarMenu
      title={t(l.header.Shop.Header)}
      size="xsmall"
      triggerStyles={styles.trigger}
      onClick={onMenuClick}
    >
      <div css={styles.content} role="menu">
        {renderShopMenu()}
      </div>
      <Spacer height="one" />
    </SidebarMenu>
  );
};

export { Shop };
